import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Asociado } from '@lib/types';
import { CAMPO_OPCIONES } from '@lib/catalogs';
import {
  getAsociado,
  type ObservacionItem,
  type ResolverObservacionBody,
} from '@lib/services/observaciones.service';
import { CampoControl } from './EditAsociadoModal';
import { FIELDS, validarFormTitular } from './asociado-campos';

interface Props {
  item: ObservacionItem;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (body: ResolverObservacionBody) => void;
}

/** El campo reportado puede venir con el alias 'apellido' → 'apellSolt'. */
function campoReportadoKey(campo: string): string {
  return campo === 'apellido' ? 'apellSolt' : campo;
}

/**
 * Modal para resolver una observación EDITANDO todos los datos del asociado.
 * Trae el asociado completo, muestra todos los campos (pre-cargando el campo
 * reportado con el valor propuesto y resaltándolo) y al guardar manda solo el
 * diff con accion 'editar-campos'. "Rechazar" marca resuelta sin tocar nada.
 *
 * Solo lo abren usuarios con permiso de edición (el botón "Resolver" está
 * gateado por useCanMutate; el server revalida con assertPuedeMutarObservacion).
 */
export function ResolverObservacionModal({ item, submitting, error, onClose, onSubmit }: Props) {
  const q = useQuery({
    queryKey: ['asociado', item.asociadoId],
    queryFn: () => getAsociado(item.asociadoId),
    enabled: !!item.asociadoId,
  });

  return (
    <div className="modalBackdrop" onClick={onClose}>
      <div className="modalCard" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <header className="modalHeader">
          <div>
            <h2 className="heading-5" style={{ margin: 0 }}>
              Resolver observación — editar datos
            </h2>
            <p className="text-element-mid" style={{ marginTop: 6, fontSize: 13 }}>
              Legajo <strong>{item.legajoNumero}</strong> — {item.asociadoNombre || 'sin nombre'} (DNI{' '}
              {item.asociadoDocumento || '—'})
            </p>
          </div>
          <button className="modalClose" onClick={onClose}>
            ×
          </button>
        </header>

        {q.isLoading && (
          <p className="text-element-mid" style={{ padding: '12px 0' }}>
            Cargando datos del asociado…
          </p>
        )}
        {q.isError && (
          <p className="status status-error" style={{ display: 'block', marginTop: 12 }}>
            No se pudieron cargar los datos del asociado.
          </p>
        )}
        {q.data && (
          <Formulario
            asociado={q.data}
            item={item}
            submitting={submitting}
            error={error}
            onClose={onClose}
            onSubmit={onSubmit}
          />
        )}
      </div>
    </div>
  );
}

function Formulario({
  asociado,
  item,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  asociado: Asociado;
  item: ObservacionItem;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (body: ResolverObservacionBody) => void;
}) {
  const reportedKey = campoReportadoKey(item.campo);

  const original = useMemo(() => {
    const o: Record<string, string> = {};
    FIELDS.forEach((f) => {
      o[f.key as string] = (asociado[f.key] ?? '').toString();
    });
    return o;
  }, [asociado]);

  const [form, setForm] = useState<Record<string, string>>(() => {
    const f = { ...original };
    // Pre-cargamos el campo reportado con el valor propuesto por el titular
    // (la observación es justamente un pedido de cambiarlo a ese valor).
    if (item.valorPropuesto && reportedKey in f) f[reportedKey] = item.valorPropuesto;
    return f;
  });
  const [vErrors, setVErrors] = useState<Record<string, string>>({});
  const [nota, setNota] = useState('');

  const esTitular = (form.parentesco ?? asociado.parentesco) === '0';

  const update = (k: string, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (vErrors[k]) {
      setVErrors((p) => {
        const n = { ...p };
        delete n[k];
        return n;
      });
    }
  };

  const diff = useMemo(() => {
    const d: Record<string, string> = {};
    for (const f of FIELDS) {
      const k = f.key as string;
      if ((form[k] ?? '').trim() !== (original[k] ?? '').trim()) {
        d[k] = (form[k] ?? '').trim();
      }
    }
    return d;
  }, [form, original]);

  const handleGuardar = () => {
    const errs = esTitular ? validarFormTitular(form) : {};
    setVErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const notaT = nota.trim();
    if (Object.keys(diff).length === 0) {
      // Nada cambió → solo marcamos resuelta (sin tocar datos).
      onSubmit({ accion: 'rechazar', ...(notaT ? { nota: notaT } : {}) });
      return;
    }
    onSubmit({ accion: 'editar-campos', campos: diff, ...(notaT ? { nota: notaT } : {}) });
  };

  const handleRechazar = () => {
    const notaT = nota.trim();
    onSubmit({ accion: 'rechazar', ...(notaT ? { nota: notaT } : {}) });
  };

  return (
    <>
      {/* Contexto de la observación */}
      <div
        style={{
          background: 'var(--color-surface-alt, #F8FAFC)',
          border: '1px solid var(--color-border, #E2E8F0)',
          borderRadius: 8,
          padding: '10px 12px',
          margin: '4px 0 14px',
          fontSize: 13,
        }}
      >
        <div>
          El titular reportó <strong>{item.campo}</strong>:{' '}
          <span style={{ color: 'var(--color-element-mid)' }}>actual</span> "{item.valorActual || '—'}"{' '}
          → <span style={{ color: 'var(--color-element-mid)' }}>propuesto</span>{' '}
          <strong style={{ color: '#166534' }}>"{item.valorPropuesto || '—'}"</strong>
        </div>
        {item.mensaje && (
          <div style={{ marginTop: 4, fontStyle: 'italic', color: 'var(--color-element-mid)' }}>
            “{item.mensaje}”
          </div>
        )}
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-element-mid)' }}>
          Podés corregir cualquier dato. El campo reportado viene pre-cargado con el valor propuesto.
        </div>
      </div>

      {/* Todos los campos editables */}
      <div className="modalGrid">
        {FIELDS.map((f) => {
          const k = f.key as string;
          const opciones = CAMPO_OPCIONES[k];
          const esReportado = k === reportedKey;
          const vErr = vErrors[k];
          const labelExtra = esTitular && k === 'email' ? ' *' : '';
          return (
            <label
              key={k}
              className="formField"
              style={
                esReportado
                  ? {
                      outline: '2px solid var(--color-primary, #2563EB)',
                      outlineOffset: 2,
                      borderRadius: 6,
                    }
                  : undefined
              }
            >
              <span>
                {f.label}
                {labelExtra}
                {esReportado && (
                  <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--color-primary, #2563EB)' }}>
                    (reportado)
                  </span>
                )}
              </span>
              <CampoControl
                fieldKey={k}
                value={form[k] ?? ''}
                placeholder={f.placeholder}
                hasError={!!vErr}
                opciones={opciones}
                onChange={(v) => update(k, v)}
              />
              {vErr && (
                <span
                  className="status status-error"
                  style={{ display: 'block', fontSize: 12, marginTop: 4 }}
                >
                  {vErr}
                </span>
              )}
            </label>
          );
        })}
      </div>

      <label style={{ display: 'block', marginTop: 14 }}>
        <span style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>Nota interna (opcional)</span>
        <input
          className="input"
          type="text"
          value={nota}
          maxLength={500}
          onChange={(e) => setNota(e.target.value)}
          placeholder='Ej. "Verificado con RRHH", "Corregido CUIL y fecha"'
        />
      </label>

      {error && (
        <p className="status status-error" style={{ display: 'block', marginTop: 12 }}>
          {error}
        </p>
      )}

      <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
          Cancelar
        </button>
        <button type="button" className="btn btn-ghost" onClick={handleRechazar} disabled={submitting}>
          Rechazar (sin cambios)
        </button>
        <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={submitting}>
          <span className="btn-font-style">{submitting ? 'Guardando…' : 'Guardar cambios'}</span>
        </button>
      </footer>
    </>
  );
}
